import { useEffect } from 'react'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import { incrementQuickCounter } from '@mobi/utils/logging/quickCounter'
import { isReactNativeApp } from '@mobi/web-native-comms/web'
import { checkNotificationStatusAsync } from '@core/Components/PushNotification/NotificationAuthPopup'
import { post } from '@core/Services/ApiService'
import { logError } from '../logging/logging'
import { useFeature } from './useFeature'

// register once every session only, and also once per 24 hours only
// stop after attempting to register for that user 7x
let didAlreadyRegister = false
const oneDayInMilliseconds = 24 * 3600 * 1000
const registrationAttemptLimit = 7

export function useRegisterForMicrosoftDynamicsPushNotification(accountNumber: number | null) {
  // note: this is excluded from deps list - we don't want to trigger instantly when the
  // flag changes
  const isDynamicsPushActive = useFeature(FeatureFlags.DYNAMICS_PUSH_REGISTRATION.key)

  useEffect(() => {
    if (!isReactNativeApp && process.env.NODE_ENV !== 'development') {
      return
    }

    if (!isDynamicsPushActive) {
      return
    }

    if (!accountNumber) {
      return
    }

    const registrationHistory = accountNumber != null ? getRegistrationHistory(accountNumber) : null
    const lastAttemptTime = registrationHistory?.lastAttemptTime ?? 0
    const attemptCount = registrationHistory?.attemptCount ?? 0

    if (didAlreadyRegister) {
      return
    }

    if (attemptCount >= registrationAttemptLimit) {
      return
    }

    if (Date.now() - lastAttemptTime < oneDayInMilliseconds) {
      return
    }

    registerForMicrosoftDynamicsPushNotification()
      .catch(e => {
        logError(`dynamicsPushRegisterDevice failed ${e.message}`, true)
        incrementQuickCounter('dynamics-push-registration-failure')
      })
      .finally(() => {
        didAlreadyRegister = true
        setRegistrationHistory(accountNumber, attemptCount + 1)
        incrementQuickCounter('dynamics-push-registration-attempt')
      })
  }, [accountNumber])
}

export async function registerForMicrosoftDynamicsPushNotification() {
  const pushStatus = await checkNotificationStatusAsync()
  const token = pushStatus.apnsRegistrationToken ?? pushStatus.fcmRegistrationToken
  return token ? await dynamicsPushRegisterDevice(token) : undefined
}

function setRegistrationHistory(accountNumber: number, attemptCount: number) {
  const key = registrationHistoryLocalStorageKey(accountNumber)
  const data: DynamicsRegistrationHistory = { lastAttemptTime: Date.now(), attemptCount }
  localStorage.setItem(key, JSON.stringify(data))
}

function getRegistrationHistory(accountNumber: number) {
  const key = registrationHistoryLocalStorageKey(accountNumber)
  const json = localStorage.getItem(key)

  try {
    if (json) {
      const data = JSON.parse(json)
      if (typeof data === 'object') {
        return data as DynamicsRegistrationHistory
      }
    }
  } catch {
    return null
  }

  return null
}

function registrationHistoryLocalStorageKey(accountNumber: number) {
  return `dynamicsPushNotification:lastAttempt:${accountNumber}`
}

function dynamicsPushRegisterDevice(token: string) {
  return post<{ RegistrationRequestId: string }>({
    url: '/api/v2/dynamics-push/register-device',
    body: { token },
  })
}

type DynamicsRegistrationHistory = {
  lastAttemptTime: number
  attemptCount: number
}
